/**
 * Sentry Error Monitoring
 *
 * 프로덕션 에러 추적 및 성능 모니터링.
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */

import * as Sentry from '@sentry/react';

// ============================================================================
// INITIALIZATION
// ============================================================================

const SENTRY_DSN = import.meta.env['VITE_SENTRY_DSN'] as string | undefined;

/**
 * Initialize Sentry
 * Call this once at app startup (in root.tsx or entry.client.tsx)
 */
let initialized = false;

export function initSentry() {
  // Skip if no DSN configured (local development)
  if (!SENTRY_DSN) {
    return;
  }

  // HMR 재실행 시 중복 초기화 방지
  if (initialized) {
    return;
  }
  initialized = true;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Sample rates
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% on error

    // ISSUE_063: 오프라인이면 전송 자체를 차단 — ERR_INTERNET_DISCONNECTED 방지
    transport: (options) => {
      const defaultTransport = Sentry.makeFetchTransport(options);
      return {
        ...defaultTransport,
        send: (envelope: Parameters<ReturnType<typeof Sentry.makeFetchTransport>['send']>[0]) => {
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return Promise.resolve({ statusCode: 200 } as { statusCode: number });
          }
          return defaultTransport.send(envelope);
        },
      };
    },

    // Filter out noise
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors (user's connection issue)
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'Network is offline',
      // User cancellation
      'AbortError',
      // React hydration (usually harmless)
      'Hydration failed',
      'Text content does not match',
    ],

    // Before sending, add context
    beforeSend(event) {
      // ISSUE_063: 오프라인이면 이벤트 드롭
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return null;
      }

      // Add user context if available
      const authStore = localStorage.getItem('noten_auth_store');
      if (authStore) {
        try {
          const { state } = JSON.parse(authStore);
          if (state?.profile) {
            Sentry.setUser({
              id: state.profile.id,
              username: state.profile.name,
            });
          }
        } catch {
          // Ignore parsing errors
        }
      }

      return event;
    },

    // ISSUE_063: 오프라인이면 트랜잭션도 드롭
    beforeSendTransaction(event) {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return null;
      }
      return event;
    },
  });

}

// ============================================================================
// EXPORTS
// ============================================================================

export { Sentry };

/**
 * Capture exception with additional context
 */
export function captureException(
  error: unknown,
  context?: Record<string, unknown>
) {
  if (context) {
    Sentry.captureException(error, { extra: context });
  } else {
    Sentry.captureException(error);
  }
}

/**
 * Capture message (for non-error events)
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info'
) {
  Sentry.captureMessage(message, level);
}

/**
 * Set user context
 */
export function setUser(user: { id: string; name?: string; email?: string } | null) {
  if (user) {
    const sentryUser: { id: string; username?: string; email?: string } = { id: user.id };
    if (user.name) sentryUser.username = user.name;
    if (user.email) sentryUser.email = user.email;
    Sentry.setUser(sentryUser);
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>
) {
  const breadcrumb: Sentry.Breadcrumb = { message, level: 'info' };
  if (category) breadcrumb.category = category;
  if (data) breadcrumb.data = data;
  Sentry.addBreadcrumb(breadcrumb);
}
